import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import {
  actesFileLink, actesReceptionInfo, actesAttachReceived, actesDocuments,
  type ActeRow, type ActesReference,
} from '@/integrations/actesApi';
import { ActeDocuments } from './ActeDocuments';
import { ActesWho, peopleReader } from './ActesPerson';
import { DocViewer } from './DocViewer';
import { Notes } from './Notes';
import { fmtDay } from './dates';
import { countryOfRef, actesEn } from '@/lib/actesCountry';

/**
 * LA FICHE D'UNE DEMANDE — en lecture.
 *
 * The form is for changing things; this is for reading them, which is what
 * anybody wants nine times out of ten: what was asked for, from whom, where it
 * stands, the four dates, the documents and what the team has said about it.
 * Editing is one button away.
 *
 * The refresh arrow is the piece worth keeping: it re-reads the received
 * document and updates the dates from what the paper actually says. When a
 * demande carries more than one received document it asks WHICH to read rather
 * than guessing — reading the wrong scan silently rewrites four dates.
 */
export function ActeView({ lang, reference, row, onClose, onEdit, onOpenDossier }: {
  lang: Lang; reference: ActesReference; row: ActeRow;
  onClose: () => void; onEdit: () => void; onOpenDossier?: (id: string) => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const [dates, setDates] = useState({
    date_demande: row.date_demande, date_reception: row.date_reception,
    date_emission: row.date_emission, date_expiration: row.date_expiration,
  });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [choices, setChoices] = useState<{ id?: string; name: string }[] | null>(null);
  const [viewer, setViewer] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const recv = useQuery({
    queryKey: ['actes', 'reception-info', row.id],
    queryFn: ({ signal }) => actesReceptionInfo(row.id, signal),
    enabled: !!row.file_path,
    staleTime: 60_000,
    retry: false,
  });


  const maps = useMemo(() => ({
    types: new Map(reference.acteTypes.map((r) => [r.id, r])),
    authorities: new Map(reference.authorities.map((r) => [r.id, r])),
    statuses: new Map(reference.acteStatuses.map((r) => [r.id, r])),
    /* La principale ET celles d'à côté (`assigned_emails`). */
    people: peopleReader(reference.profiles),
  }), [reference]);
  const status = row.status_id ? maps.statuses.get(row.status_id) : null;
  const team = maps.people.people(row);
  const label = (r?: { label_fr: string; label_en: string }) => (r ? (lang === 'fr' ? r.label_fr : r.label_en) : '—');
  const fmt = (d: unknown) => fmtDay(d, lang);

  /* More than one received document? Ask which to read. Reading the wrong scan
     silently rewrites four dates, and that is not a mistake anybody notices. */
  const askRefresh = async () => {
    setNote(null); setErr('');
    try {
      const docs = await actesDocuments(row.id);
      const extra = docs.filter((d) => d.source === 'ai_reception');
      if (!extra.length) { void refresh(); return; }
      setChoices([
        { name: row.file_name ?? T('Document reçu', 'Received document') },
        ...extra.map((d) => ({ id: d.id, name: d.file_name ?? T('Document', 'Document') })),
      ]);
    } catch { void refresh(); }
  };

  const refresh = async (readDocId?: string) => {
    setChoices(null); setBusy(true); setNote(null); setErr('');
    try {
      const r = await actesAttachReceived(row.id, readDocId ? { read_doc_id: readDocId } : {}) as unknown as {
        read?: boolean; date_emission?: string | null; date_expiration?: string | null; date_reception?: string | null;
        /* Le motif de l'échec, que le serveur envoyait dans le vide. */
        detail?: string;
      };
      if (r.read) {
        setDates((d) => ({
          ...d,
          date_emission: r.date_emission ?? d.date_emission,
          date_expiration: r.date_expiration ?? d.date_expiration,
          date_reception: r.date_reception ?? d.date_reception,
        }));
        setNote({ ok: true, text: T('Dates mises à jour depuis le document.', 'Dates updated from the document.') });
        await qc.invalidateQueries({ queryKey: ['actes'] });
      } else {
        if (r.date_reception) setDates((d) => ({ ...d, date_reception: r.date_reception as string }));
        setNote({ ok: false, text: [
          T('La lecture n’a rien pu tirer de ce document.', 'The reading could not get anything from this document.'),
          r.detail,
        ].filter(Boolean).join(' — ') });
      }
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };


  return createPortal(
    <div className="svc-modal-backdrop actes-skin" onClick={onClose}>
      <div className="svc-modal actes-wide sm" onClick={(e) => e.stopPropagation()}>
        <button className="svc-modal-close" onClick={onClose}>×</button>
        <div className="svc-modal-body">
          <div className="svc-modal-tag">{T('Demande d’acte', 'Civil-record request')}</div>
          <div className="actes-tabline">
            <h2 style={{ marginBottom: 0 }}>{row.person_concerned}</h2>
            {reference.me.isStaff && (
              <button className="btn btn-sm btn-ghost" onClick={onEdit}>
                <KBIcon name="edit" size={13} /> {T('Modifier', 'Edit')}
              </button>
            )}
          </div>
          <div className="actes-view-sub">
            {status && <span className={`actes-tag c-${status.color || 'slate'}`}>{label(status)}</span>}
            {row.dossier_id && onOpenDossier
              ? <button className="actes-linkbtn" onClick={() => { onOpenDossier(row.dossier_id as string); onClose(); }}>
                  {row.dossier_name || '—'} <KBIcon name="ext" size={11} />
                </button>
              : <span className="dim">{row.dossier_name || T('Sans dossier', 'No dossier')}</span>}
          </div>

          <dl className="actes-view-grid">
            <Field label={T('Type d’acte', 'Type')} value={label(maps.types.get(row.acte_type_id ?? -1))} />
            {countryOfRef(reference) === 'PT' ? (<>
              {/* Portugal: what was asked for, in words; issued here or abroad. */}
              <Field label="Document requested" value={row.request_line || '—'} />
              <Field label="Origin" value={row.is_foreign ? 'Foreign document' : 'Portuguese document'} />
            </>) : (<>
            <Field label={T('Autorité', 'Authority')} value={label(maps.authorities.get(row.authority_id ?? -1))} />
            <Field label={T('Origine', 'Origin')}
              value={row.is_foreign ? T('Acte étranger', 'Foreign act') : T('Acte français', 'French act')} />
            </>)}
            <Field label={T('Référence', 'Reference')} value={row.reference || '—'} />
            <Field label={T('N° (ordre)', 'No. (order)')} value={row.numero || '—'} />
            <Field label={T('Demandé par', 'Requested via')}
              value={row.demande_channel === 'courrier' ? T('Courrier', 'Post')
                : row.demande_channel === 'email' ? T('E-mail', 'E-mail') : '—'} />
            <div className="actes-view-f">
              <dt>{team.length > 1 ? T('Responsables', 'Owners') : T('Responsable', 'Owner')}</dt>
              <dd className="actes-view-team">{team.length ? team.map((p) => <ActesWho key={p.email || p.id} person={p} />) : '—'}</dd>
            </div>
          </dl>

          {/* ── Les quatre dates ── */}
          <div className="actes-fieldset">
            <div className="actes-tabline">
              <span className="cap">{T('Dates', 'Dates')}</span>
              {row.file_path && (
                <button className="actes-row-act" disabled={busy} onClick={() => void askRefresh()}
                  title={T('Relire le document et remettre les dates à jour', 'Re-read the document and refresh the dates')}>
                  <KBIcon name="reset" size={13} />
                </button>
              )}
            </div>
            <dl className="actes-view-grid four">
              <Field label={T('Demandée', 'Requested')} value={fmt(dates.date_demande)} />
              <Field label={T('Réception', 'Received')} value={fmt(dates.date_reception)} />
              <Field label={T('Émission', 'Issued')} value={fmt(dates.date_emission)} />
              <Field label={T('Expiration', 'Expires')} value={fmt(dates.date_expiration)} />
            </dl>
            {note && <p className={`muted-note ${note.ok ? 'ok-note' : 'warn-note'}`}>{note.text}</p>}
            {choices && (
              <div className="actes-docs-list">
                <p className="muted-note">{T('Quel document relire ?', 'Which document should be re-read?')}</p>
                {choices.map((c) => (
                  <button key={c.id ?? 'main'} className="actes-suggest-row" onClick={() => void refresh(c.id)}>
                    <KBIcon name="documents" size={13} />
                    <span className="actes-list-main"><strong>{c.name}</strong></span>
                  </button>
                ))}
                <button className="btn btn-sm btn-ghost" onClick={() => setChoices(null)}>{T('Annuler', 'Cancel')}</button>
              </div>
            )}
          </div>

          {/* ── Les documents ── */}
          <div className="actes-fieldset">
            <span className="cap">{T('Documents', 'Documents')}</span>
            {row.file_path ? (
              <div className="actes-docs-list">
                <div className="actes-docs-one">
                  <KBIcon name="documents" size={14} />
                  <span className="actes-docs-n">{row.file_name || T('Document reçu', 'Received document')}</span>
                  {recv.data?.uploader_name && <span className="dim">· {recv.data.uploader_name}</span>}
                  <button className="actes-row-act" title={T('Voir', 'View')} onClick={() => setViewer(true)}>
                    <KBIcon name="eye" size={13} />
                  </button>
                </div>
              </div>
            ) : (
              <p className="muted-note">{T('Aucun document reçu.', 'No received document.')}</p>
            )}
            <ActeDocuments lang={lang} acteId={row.id} source="ai_reception" canManage={false} hideEmpty />
            <ActeDocuments lang={lang} acteId={row.id} source="manual" canManage={false} hideEmpty />
          </div>

          {row.comments && (
            <div className="actes-fieldset">
              <span className="cap">{T('Commentaires', 'Comments')}</span>
              <p className="actes-view-text">{row.comments}</p>
            </div>
          )}

          {/* ── Les notes de l'équipe ──
              En lecture, le cadre d'écriture ouvert en permanence prenait deux
              cents pixels sous « Aucune note » : c'est une fiche qu'on vient
              LIRE. Il attend derrière un bouton. */}
          <div className="actes-fieldset">
            <span className="cap">{T('Notes', 'Notes')}</span>
            <Notes lang={lang} reference={reference} acteId={row.id} compact />
          </div>

          {err && <p className="muted-note ni-err">⚠ {err}</p>}
        </div>
      </div>
      {viewer && <DocViewer lang={lang} load={() => actesFileLink(row.id)} onClose={() => setViewer(false)} />}
    </div>,
    document.body,
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="actes-view-f">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}
